import type { IFetch, IRequestConfig } from '@types'
import type { IRogue, IRogueApi } from '../RogueTypes'

let failedRefreshAttempts = 0

const ServiceWithSiteParam = [
  'svc_content',
  'svc_checkout',
  'svc_gc',
  'svc_paymentmethod',
  'svc_extrapayment',
  'svc_push',
  'svc_tax',
  'svc_bin',
  'svc_route',
  'svc_list',
  'svc_customer',
  'svc_session',
  'svc_programming',
  'svc_ticket',
  'svc_rss',
  'svc_rma',
  'svc_event',
]
export class RogueApi implements IRogueApi {
  instance: IFetch

  refreshPromise?: Promise<void>

  rogue?: IRogue

  baseUrl?: string

  private lastRefresh: number = 0

  constructor(instance: IFetch) {
    this.instance = instance
  }

  init(rogue: IRogue) {
    this.rogue = rogue
  }

  refreshSession(): Promise<void> | undefined {
    if (!this.refreshPromise) {
      this.refreshPromise = this.fetch({ url: '/svc_session/session' })
        .then(() => {
          failedRefreshAttempts = 0
          this.lastRefresh = Date.now()
          this.rogue?.log.crumb('auth', 'Token refreshed after API failure')
        })
        .catch((err: any) => {
          const delay = Math.min((failedRefreshAttempts * 10) ** 2, 5000)
          failedRefreshAttempts += 1
          this.lastRefresh = Date.now()

          this.rogue?.log.warn(`Failed auth API call (${failedRefreshAttempts}) - triggering logout after ${delay}`, err)

          return new Promise<void>((resolve) => setTimeout(async () => {
            if (this.rogue) {
              const { useCustomerStore } = await import('@/js/stores/customerStore')
              const customerStore = useCustomerStore()
              customerStore.logout(this.rogue)
            }
            resolve()
          }, delay))
        })
        .then(() => this.refreshPromise = undefined)
    }

    return this.refreshPromise
  }

  async fetch<T>(config: IRequestConfig): Promise<T> {
    const { site, ssrContext, l10n } = this.rogue!

    // If this is rendering a non-US site on the US domain, we may need to modify our request...
    if (l10n.path && site.code !== 'USA' && config.url.indexOf(`/site/${site.code.toUpperCase()}`) === -1) {
      const parts = config.url.split('/')
      const microservice = parts[1]

      // If this is a microservice that requires the site, add it after the microservice identifier
      if (ServiceWithSiteParam.includes(microservice) || (microservice === 'svc_ssr' && ['heartbeat', 'rfktkn'].includes(parts[2]))) {
        parts.splice(2, 0, 'site', site.code)
        config.url = parts.join('/')
      }
    }

    return this.instance.request<T>(config, site, ssrContext)
      .catch(e => {
        // @todo Are there other global error types we can handle here like Cloudflare CAPTCHA check?
        this.rogue?.log.crumb('xhr', `Failed ${config.method} request to ${config.url.split('?')[0]}: ${e.status} ${e.message || JSON.stringify(e.data)}`)

        if (!this.rogue?.ssr && e.message === 'browser_offline') {
          throw e
        }

        if (!this.rogue?.ssr && e.error === 'TOKEN_EXPIRED') {
          // This error can be thrown by lots of microservices if the JWT / session token has expired.
          // We just need to refresh the session - then we can retry our original request
          this.rogue?.log.crumb('auth', 'Auth expired unexpectedly, refreshing via api call')

          if (config.retries && config.retries > 3) {
            throw e
          }

          const timeout = new Promise(resolve => setTimeout(resolve, (config.retries || 0) * 500))

          config.retries = config.retries || 0
          config.retries += 1

          return timeout.then(() => this.refreshSession()).then(() => this.fetch(config))
        }

        throw e
      })
  }
}
